import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import CharacterCount from './CharacterCount';
import FieldErrorMessage from './FieldErrorMessage';

interface BaseFieldProps<T extends FieldValues> {
  name: Path<T>;
  label?: React.ReactNode;
  maxLength?: number;
  placeholder?: string;
  errors?: string[];
  labelPosition?: 'top' | 'left';
}

interface FormFieldProps<T extends FieldValues, C extends React.ElementType>
  extends BaseFieldProps<T> {
  control: Control<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.ComponentType<any>;
  componentProps?: Partial<React.ComponentProps<C>>;
  fieldRef?: (node: HTMLDivElement | null) => void;
}

function FormField<T extends FieldValues, C extends React.ElementType = React.ElementType>({
  name,
  label,
  maxLength,
  placeholder,
  component: Component,
  control,
  errors,
  componentProps,
  fieldRef,
  labelPosition = 'top',
}: FormFieldProps<T, C>) {
  const containerClass =
    labelPosition === 'left' ? 'flex items-center gap-4' : 'flex flex-col gap-[6px]';
  return (
    <div
      ref={fieldRef}
      className={containerClass}
    >
      {label && <div className='text-sm'>{label}</div>}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <Component
              {...field}
              {...componentProps}
              placeholder={placeholder}
              error={fieldState.error}
            />
            <div className='flex justify-between'>
              {(errors || maxLength) && (
                <>
                  <div className='flex-1'>{errors && <FieldErrorMessage errors={errors} />}</div>
                  {maxLength && (
                    <CharacterCount
                      current={String(field.value || '').length}
                      max={maxLength}
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}
      />
    </div>
  );
}

export default FormField;
